import { Box, StarFilledIcon, Typography } from '@iheartjane/reefer';
import type { PaddingProperties } from '@iheartjane/reefer';
import type { Nullish } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { productCardTextDetails } from '../../utils/productCard/textDetails';
import type { TextDetailsArgs } from '../../utils/productCard/textDetails';

import styles from './cardText.module.css';

export interface CardTextProps extends TextDetailsArgs, PaddingProperties {
  /**
   * Indicates product rating (out of 5 stars) and number of reviews.
   * @example "4.9 (1.2k)"
   */
  ratingSummary?: Nullish<string>;
  /**
   * Render prop for the product category label (first line).
   * Receives a classname to apply to the label container, to align text.
   */
  renderProductCategoryLabel: ({
    className,
  }: {
    className: string;
  }) => ReactNode;
}

export const CardText = ({
  brandInfo,
  dosageInfo,
  hasMultipleWeightDiscount,
  isSponsored,
  name,
  potencyInfo,
  ratingSummary,
  renderProductCategoryLabel,
  ...paddingProps
}: CardTextProps) => {
  const lines = useMemo(
    () =>
      productCardTextDetails({
        brandInfo,
        dosageInfo,
        hasMultipleWeightDiscount,
        isSponsored,
        name,
        potencyInfo,
      }),
    [
      brandInfo,
      dosageInfo,
      hasMultipleWeightDiscount,
      isSponsored,
      name,
      potencyInfo,
    ]
  );

  return (
    <Box className={styles['cardText']} {...paddingProps}>
      {renderProductCategoryLabel({ className: styles['line--first'] })}
      {ratingSummary ? (
        <div className={styles['reviews']}>
          <StarFilledIcon color="gold" size="sm" mr={2} />
          <Typography
            maxLines={1}
            variant="small-body-regular"
            color="text-main"
          >
            {ratingSummary}
          </Typography>
        </div>
      ) : null}

      {lines.map(({ text, ...typographyProps }, index) => (
        <Typography
          className={styles['line--full']}
          key={`${text}-${index}`}
          {...typographyProps}
        >
          {text}
        </Typography>
      ))}
    </Box>
  );
};
